import React, { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { PublicRoutes } from "./public-routes";
import { PrivateRoutes } from "./private-routes";
import LoginPage from "src/pages/public/login";
import ForgotPasswordPage from "src/pages/public/forgot-password";
import { Layout } from "src/shared/layout";
import ActiveEmployeesPage from "src/pages/private/employees/active";
import ActiveLocationsPage from "src/pages/private/locations/active";
import ArchivedEmployeesPage from "src/pages/private/employees/archived";
import AddEditEmployeePage from "src/pages/private/employees/add-edit-employee";
import ArchivedLocationsPage from "src/pages/private/locations/archived";
import AddEditLocationPage from "src/pages/private/locations/add-edit-location";
import TransactionsListPage from "src/pages/private/transactions/list";
import BITransactionsPage from "src/pages/private/business-intelligence/transactions";
import { doc, onSnapshot } from "firebase/firestore";
import { authentication, db } from "src/firebase";
import { getFirstPrivateRoute } from "src/utils";
import { useRecoilState } from "recoil";
import { authState } from "src/store/auth";
import { IUserPermissions } from "src/types/generic";
import { LinearProgress } from "@mui/material";
import NoAccess from "src/shared/components/no-access";
import { signOut } from "firebase/auth";
import { notificationsState } from "src/store/notifications";
import LocationsProfilePage from "src/pages/private/locations/location-profile";
import ReportsSalesSummaryPage from "src/pages/private/reports/sales-summary";
import CustomersFeedbackPage from "src/pages/private/customers/feedback";
import ActiveClientsPage from "src/pages/private/clients/active";
import AddEditClientsPage from "src/pages/private/clients/add-edit-clients";
import ArchivedClientsPage from "src/pages/private/clients/archived";
import ClientProfilePage from "src/pages/private/clients/client-profile";
import TransactionsUpcomingListPage from "src/pages/private/transactions/upcoming";
import KAFDReportPage from "src/pages/private/reports/kafd-api-report";
import EmployeesShiftsPage from "src/pages/private/employees/shifts";
import TicketsValidationPage from "src/pages/private/transactions/tickets-validation";
import TrackingEmployeesPage from "src/pages/private/employees/tracking";
import CustomersList from "src/pages/private/customers/list";
import ActiveDriversList from "src/pages/private/delivery/active";
import AddEditDriverPage from "src/pages/private/delivery/add-edit-driver";
import ArchivedDriversList from "src/pages/private/delivery/archived";
import GolfCartRequestsPage from "src/pages/private/transactions/golf-requests";
import AddEditSunmiAccount from "src/pages/private/employees/add-edd-sunmi";

function AllRoutes() {
  const [auth, setAuth] = useRecoilState(authState);
  const [permissions, setPermissions] = useState<IUserPermissions>();
  const [firstRouteToAccess, setFirstRouteToAccess] = useState<
    string | undefined
  >();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const forceLogout = (message: string) => {
    signOut(authentication).finally(() => {
      setNotifications((prev) => [...prev, { type: "error", message }]);
      localStorage.clear();
      window.location.replace("/login");
    });
  };

  useEffect(() => {
    if (!auth.user) return;

    setLoading(true);

    const docRef = doc(db, "users", auth.user.uid);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          forceLogout("User not found");
          return;
        }

        const userData = docSnap.data();
        console.log({ userData });
        const userPermissions = userData.permissions;

        if (!userPermissions || !userData.isActive) {
          forceLogout("You no longer have access");
          return;
        }

        setPermissions(userPermissions);
        setFirstRouteToAccess(getFirstPrivateRoute(userPermissions));

        const role = userData.isSuperAdmin
          ? "Super Admin"
          : userData.isAdmin
            ? "Admin"
            : userData.locationType;

        setAuth((prev) => ({
          ...prev,
          permissions: userPermissions,
          user: {
            ...prev.user,
            roleType: role,
            allowedLocationsId: userData?.allowedLocationsId ?? [],
          },
        }));
        setLoading(false);
      },
      (error) => {
        console.error("Permission listener error:", error);
        forceLogout("Permission error");
      },
    );

    return () => unsubscribe();
  }, [auth.user?.uid]);

  return loading || (!!auth.user && !auth.permissions) ? (
    <LinearProgress />
  ) : (
    <BrowserRouter>
      <Routes>
        <Route
          element={<PublicRoutes firstRouteToAccess={firstRouteToAccess} />}
        >
          <Route element={<LoginPage />} path="/login" />
          <Route element={<LoginPage />} path="/" />
          <Route element={<ForgotPasswordPage />} path="/forgot-password" />
        </Route>
        <Route element={<PrivateRoutes />}>
          <Route element={<Layout />}>
            {/* locations */}
            <Route
              element={
                permissions?.locations?.view || loading ? (
                  <ActiveLocationsPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/locations/active"
            />
            <Route
              element={
                permissions?.locations?.view || loading ? (
                  <ArchivedLocationsPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/locations/archived"
            />
            <Route
              element={
                permissions?.locations?.create || loading ? (
                  <AddEditLocationPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/locations/active/add"
            />
            <Route
              element={
                permissions?.locations?.edit || loading ? (
                  <AddEditLocationPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/locations/active/:id/edit"
            />
            <Route
              element={
                permissions?.locations?.edit || loading ? (
                  <AddEditLocationPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/locations/archived/:id/edit"
            />
            {/* TODO: just UI; not integrated yet */}
            <Route
              element={<LocationsProfilePage />}
              path="/locations/:id/profile"
            />
            {/* employees */}
            <Route
              element={
                permissions?.arsannEmployees?.view || loading ? (
                  <ActiveEmployeesPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/active"
            />
            <Route
              element={
                permissions?.arsannEmployees?.create || loading ? (
                  <AddEditEmployeePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/active/add"
            />
            <Route
              element={
                permissions?.arsannEmployees?.edit || loading ? (
                  <AddEditEmployeePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/active/:id/edit"
            />
            <Route
              element={
                permissions?.arsannEmployees?.create || loading ? (
                  <AddEditSunmiAccount />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/active/add-sunmi"
            />
            <Route
              element={
                permissions?.arsannEmployees?.edit || loading ? (
                  <AddEditSunmiAccount />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/active/sunmi/:id/edit"
            />
            <Route
              element={
                permissions?.arsannEmployees?.edit || loading ? (
                  <AddEditSunmiAccount />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/archived/sunmi/:id/edit"
            />
            <Route
              element={
                permissions?.arsannEmployees?.view || loading ? (
                  <EmployeesShiftsPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/shifts"
            />
            <Route
              element={
                permissions?.arsannEmployees?.view || loading ? (
                  <TrackingEmployeesPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/tracking"
            />
            <Route
              element={
                permissions?.arsannEmployees?.edit || loading ? (
                  <AddEditEmployeePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/archived/:id/edit"
            />
            <Route
              element={
                permissions?.arsannEmployees?.view || loading ? (
                  <ArchivedEmployeesPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/employees/archived"
            />
            {/* transactions */}
            <Route
              element={
                permissions?.transactions?.view || loading ? (
                  <TransactionsUpcomingListPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/transactions/upcoming"
            />
            <Route
              element={
                permissions?.transactions?.view || loading ? (
                  <TransactionsListPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/transactions/list"
            />
            <Route
              element={
                permissions?.transactions?.view || loading ? (
                  <TicketsValidationPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/transactions/tickets-validation"
            />
            <Route
              element={
                true || loading ? <GolfCartRequestsPage /> : <NoAccess />
              }
              path="/transactions/golf-requests"
            />

            {/* clients */}
            <Route
              element={
                permissions?.clients?.view || loading ? (
                  <ActiveClientsPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/active"
            />
            <Route
              element={
                permissions?.clients?.view || loading ? (
                  <ArchivedClientsPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/archived"
            />
            <Route
              element={
                permissions?.clients?.create || loading ? (
                  <AddEditClientsPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/active/add"
            />

            <Route
              element={
                permissions?.clients?.view || loading ? (
                  <ClientProfilePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/active/:id"
            />

            <Route
              element={
                permissions?.clients?.view || loading ? (
                  <ClientProfilePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/:status/:id/contacts"
            />

            <Route
              element={
                permissions?.clients?.view || loading ? (
                  <ClientProfilePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/:status/:id/accounts"
            />

            <Route
              element={
                permissions?.clients?.view || loading ? (
                  <ClientProfilePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/:status/:id/locationList"
            />

            <Route
              element={
                permissions?.clients?.view || loading ? (
                  <ClientProfilePage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/archived/:id"
            />

            <Route
              element={
                permissions?.locations?.create || loading ? (
                  <AddEditLocationPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/active/:clientId/add-location"
            />
            <Route
              element={
                permissions?.locations?.create || loading ? (
                  <AddEditLocationPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/clients/active/:clientId/locationList/:id/edit"
            />
            {/* Business Intelligence */}
            <Route
              element={
                permissions?.businessIntelligence?.viewTransactions ||
                loading ? (
                  <BITransactionsPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/business-intelligence/trans"
            />
            {/* Reports */}
            <Route
              element={
                permissions?.reports?.viewSalesSummary || loading ? (
                  <ReportsSalesSummaryPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/reports/sales"
            />
            <Route
              element={
                permissions?.reports?.viewKafdSalesReport || loading ? (
                  <KAFDReportPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/reports/kafd"
            />

            <Route
              element={
                permissions?.customers?.viewFeedback ? (
                  <CustomersFeedbackPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/customers/feedback"
            />
            <Route
              element={
                permissions?.customers?.viewCustomersList ? (
                  <CustomersList />
                ) : (
                  <NoAccess />
                )
              }
              path="/customers/list"
            />

            {/* delivery */}
            <Route
              element={
                permissions?.delivery?.view || loading ? (
                  <ActiveDriversList />
                ) : (
                  <NoAccess />
                )
              }
              path="/delivery/active"
            />
            <Route
              element={
                permissions?.delivery?.view || loading ? (
                  <ArchivedDriversList />
                ) : (
                  <NoAccess />
                )
              }
              path="/delivery/archived"
            />
            <Route
              element={
                permissions?.delivery?.create || loading ? (
                  <AddEditDriverPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/delivery/active/add"
            />
            <Route
              element={
                permissions?.delivery?.edit || loading ? (
                  <AddEditDriverPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/delivery/active/:id"
            />
            <Route
              element={
                permissions?.delivery?.edit || loading ? (
                  <AddEditDriverPage />
                ) : (
                  <NoAccess />
                )
              }
              path="/delivery/archived/:id"
            />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default AllRoutes;
